import { useCallback, useEffect, useState } from "react";

import axios from "../axiosConfig";
import { BASE_SERVER_URL } from "../sampleData";
import { useAuth } from "../auth/AuthContext";
import { useActiveBusiness } from "../auth/useActiveBusiness";
import { can, permissionsOf } from "../auth/permissions";
import UsersDirectory from "../components/UsersDirectory";

/* ------------------------------------------------------------------ *
 * Staff (redesign) — /staff (was /users, which redirects here)
 *
 * Replaces src/pages/users.js (kept at /users-v1):
 *   - collapsible cards                 -> one table with search + role filter
 *   - a role dropdown in a popup        -> role cards in a centred editor
 *                                          modal; rows grouped by role
 *
 * Same endpoints as the legacy page — GET /users/getAll, POST
 * /users/addNew, POST /users/editUser, GET /users/removeUser/:id — all
 * scoped to the open business by the `business-id` header. The editor can
 * additionally send `permissions` (an explicit set, or null to clear it),
 * which the server stores on the membership.
 * ------------------------------------------------------------------ */

const withBusiness = (bizId) =>
  bizId ? { headers: { "business-id": String(bizId) } } : {};

export function UsersRedesign() {
  const auth = useAuth() || {};
  const user = auth.user;
  const active = useActiveBusiness();
  const bizId = (active && active.id) || (auth.business && auth.business.id) || null;
  const bizName =
    (active && (active.name || active.code)) ||
    (auth.business && auth.business.name) ||
    "this business";
  const bizCode = (active && active.code) || (auth.business && auth.business.code) || null;

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    axios
      .get(`${BASE_SERVER_URL}/users/getAll`, withBusiness(bizId))
      .then((res) => {
        const list = (res && res.data && res.data.response) || [];
        setUsers(
          list.map((u) => ({
            id: String(u._id),
            full_name: u.full_name,
            email: u.email,
            userName: u.userName,
            thumb: u.thumb,
            active: u.active,
            date: u.date,
            memberships: [
              {
                business_id: String(bizId || "current"),
                role: u.role || u.access,
                permissions: u.permissions,
                custom_permissions:
                  u.custom_permissions === undefined ? null : u.custom_permissions,
              },
            ],
          }))
        );
      })
      .catch((err) => {
        setUsers([]);
        setError(
          (err && err.response && err.response.data && err.response.data.message) ||
            "The server didn't answer."
        );
      })
      .finally(() => setLoading(false));
  }, [bizId]);

  useEffect(() => {
    load();
  }, [load]);

  const businesses = [
    {
      id: String(bizId || "current"),
      name: bizName,
      code: bizCode,
      can_manage: can(user, "users:manage"),
    },
  ];

  const api = {
    add: (biz, detail) =>
      axios.post(`${BASE_SERVER_URL}/users/addNew`, { userDetail: detail }, withBusiness(bizId)),
    edit: (biz, id, updateData) =>
      axios.post(`${BASE_SERVER_URL}/users/editUser`, { id, updateData }, withBusiness(bizId)),
    remove: (biz, id) =>
      axios.get(`${BASE_SERVER_URL}/users/removeUser/${id}`, withBusiness(bizId)),
  };

  return (
    <UsersDirectory
      mode="business"
      title="Staff"
      lede={`Everyone with access to ${bizName}, grouped by role.`}
      businesses={businesses}
      users={users}
      loading={loading}
      error={error}
      reload={load}
      currentUser={user}
      grantableIn={() => permissionsOf(user)}
      api={api}
    />
  );
}

export default UsersRedesign;
